<template>
    <el-form :model="form" :rules="rules" ref="formRef" label-width="120px" class="blacklist-form">
        <!-- ID（自动生成，禁止输入） -->
        <el-form-item label="ID" prop="id">
            <el-input v-model="form.id" disabled style="cursor: not-allowed;" />
        </el-form-item>

        <!-- UID -->
        <el-form-item label="B站UID/QQ" prop="uid">
            <el-input v-model="form.uid" placeholder="请输入B站UID或QQ号"></el-input>
        </el-form-item>

        <!-- 昵称 -->
        <el-form-item label="昵称" prop="name">
            <el-input v-model="form.name" placeholder="请输入用户昵称"></el-input>
        </el-form-item>

        <!-- 违规行为 -->
        <el-form-item label="违规行为" prop="violation">
            <el-select v-model="form.violation" placeholder="请选择违规行为" style="width: 100%">
                <el-option v-for="(item, index) in violationOptions" :key="index" :label="item" :value="item" />
            </el-select>
        </el-form-item>

        <!-- 处理方式 -->
        <el-form-item label="处理方式" prop="handle">
            <el-input v-model="form.handle" placeholder="请填写处理方式"></el-input>
        </el-form-item>

        <!-- 处理人（多选） -->
        <el-form-item label="处理人" prop="handleman">
            <el-select v-model="form.handleman" multiple placeholder="请选择处理人" style="width: 100%">
                <el-option v-for="user in userOptions" :key="user.id" :label="user.name" :value="user.name" />
            </el-select>
        </el-form-item>

        <!-- 图片材料（手动上传，限制1张） -->
        <el-form-item label="图片材料" prop="materials">
            <el-upload class="upload-demo" :auto-upload="false" :file-list="fileList" name="materials"
                list-type="picture" :limit="1" :on-exceed="handleExceed" :on-change="handleFileChange"
                :on-remove="handleRemove">
                <el-button type="primary" icon="Upload">选择图片</el-button>
                <template #tip>
                    <div class="el-upload__tip">
                        支持JPG、PNG格式，单张不超过10MB，最多上传1张
                    </div>
                </template>
            </el-upload>
        </el-form-item>

        <!-- 所属平台（单选） -->
        <el-form-item label="所属平台" prop="platform">
            <el-radio-group v-model="form.platform">
                <el-radio label="B站">B站</el-radio>
                <el-radio label="QQ">QQ</el-radio>
            </el-radio-group>
        </el-form-item>

        <!-- 操作按钮 -->
        <el-form-item>
            <el-button type="primary" :loading="submitLoading" @click="onSubmit">
                提交
            </el-button>
            <el-button @click="onReset">重置</el-button>
        </el-form-item>
    </el-form>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElForm, ElFormItem, ElInput, ElSelect, ElOption, ElUpload, ElButton, ElRadioGroup, ElRadio } from 'element-plus';
import axios from 'axios';

// 表单数据
const form = reactive({
    id: '',
    uid: '',
    name: '',
    violation: '',
    handle: '',
    handleman: [],
    materials: null, // 存储单个文件对象
    platform: ''
});

const violationOptions = [
    '发表不当言论',
    '骂人、吵架、人身攻击',
    '过度涩涩',
    '发送不明链接',
    '多次过度散播低气压负能量',
    '发表引战言论',
];

// 校验规则
const rules = {
    uid: [
        { required: true, message: '请输入B站UID或QQ号', trigger: 'blur' },
        { pattern: /^\d+$/, message: '请输入数字', trigger: 'blur' }
    ],
    name: [
        { required: true, message: '请输入用户昵称', trigger: 'blur' }
    ],
    violation: [
        { required: true, message: '请描述违规行为', trigger: 'blur' }
    ],
    handle: [
        { required: true, message: '请填写处理方式', trigger: 'blur' }
    ],
    handleman: [
        { required: true, message: '请选择处理人', trigger: 'change' }
    ],
    platform: [
        { required: true, message: '请选择所属平台', trigger: 'change' }
    ],
};

const formRef = ref(null);
const fileList = ref([]);
const userOptions = ref([]);
const submitLoading = ref(false);

// 生成唯一ID
const generateUniqueId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `BL${timestamp}${random}`;
};

// 获取处理人列表
const fetchUserList = async () => {
    form.id = generateUniqueId();
    try {
        const response = await axios.get('/adminapi/user/list');
        if (response.data) {
            userOptions.value = response.data.results.map(item => ({
                id: item.id,
                name: item.user
            }));
        } else {
            ElMessage.error(response.data.message || '获取处理人列表失败');
        }
    } catch (error) {
        ElMessage.error('网络错误，请重试');
    }
};

// 处理文件变化
const handleFileChange = (uploadFile, uploadFiles) => {
    // 确保只保留最新上传的文件
    fileList.value = [uploadFile];

    // 检查文件大小
    const fileSize = uploadFile.size / 1024 / 1024; // MB
    if (fileSize > 10) {
        ElMessage.warning('图片大小不能超过10MB');
        fileList.value = [];
        form.materials = null;
        return;
    }

    // 存储文件对象
    form.materials = uploadFile.raw;
};

// 处理文件移除
const handleRemove = () => {
    form.materials = null;
    fileList.value = [];
};

// 处理文件超出限制
const handleExceed = () => {
    ElMessage.warning('最多只能上传1张图片');
};

// 提交表单
const onSubmit = async () => {
    formRef.value.validate(async (valid) => {
        if (!valid) return;

        submitLoading.value = true;

        try {
            // 创建FormData
            const formData = new FormData();
            formData.append('id', form.id);
            formData.append('uid', form.uid);
            formData.append('name', form.name);
            formData.append('violation', form.violation);
            formData.append('handle', form.handle);
            formData.append('handleman', form.handleman.join(','));
            formData.append('platform', form.platform);

            // 添加图片文件（确保存在）
            if (form.materials) {
                formData.append('materials', form.materials);
            }

            // 提交请求
            const response = await axios.post(
                '/adminapi/black/add',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.code === 200) {
                ElMessage.success('提交成功！');
                onReset();
            } else {
                ElMessage.error(response.data.message || '提交失败');
            }
        } catch (error) {
            console.error('提交错误:', error);
            ElMessage.error('网络异常，请重试');
        } finally {
            submitLoading.value = false;
        }
    });
};

// 重置表单
const onReset = () => {
    formRef.value.resetFields();
    fileList.value = [];
    form.materials = null;
    form.id = generateUniqueId();
};

// 初始化
onMounted(() => {
    fetchUserList();
});
</script>

<style scoped>
.blacklist-form {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
}

.upload-demo {
    width: 100%;
}

.el-upload__tip {
    color: #909399;
    font-size: 12px;
    margin-top: 8px;
}
</style>